import { validations } from "@/utils/validationUtils";
import { Card, Col, Form, Row } from "antd";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { hasPermission } from "../../../../common/security/utils";
import { AgentType } from "../../../agent/enums";
import { useCurrentAgentAccount } from "../../../auth/hooks";
import AffiliatePartnerSelect from "../../../enumerations/components/form/AffiliatePartnerSelect";
import { AgentSelect } from "../../../enumerations/components/form/AgentSelect";
import type { Contract } from "../../types";
import { isContractVerified } from "../../utils";
import { ContractGainersFormSection } from "./ContractGainersFormSection";

const colSpan = 4;

interface Props {
  initialContract?: Contract;
  privilegedChangesPermission: Permission;
}

const ContractFormAgentsSection = ({ initialContract, privilegedChangesPermission }: Props) => {
  const { t } = useTranslation();

  const userAccount = useCurrentAgentAccount();

  const disablePrivilegedInput =
    isContractVerified(initialContract) &&
    userAccount &&
    !hasPermission(userAccount.permissions, privilegedChangesPermission);

  return (
    <Card type="inner" className="card-box card-box--inner-extra" title={t("contract.sections.agents")}>
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="signerId"
            label={t("contract.attrs.signerId")}
            rules={[validations.notNull]}
            initialValue={
              userAccount?.agent?.type === AgentType.LEGAL ? userAccount.representingAgent?.id : userAccount?.agent?.id
            }
          >
            <AgentSelect
              optionsProps={{
                defaultOption: initialContract?.signer,
                agentTypes: [AgentType.NATURAL, AgentType.SELF_EMPLOYED],
              }}
              disabled={disablePrivilegedInput}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="managerId"
            label={t("contract.attrs.managerId")}
            rules={[validations.notNull]}
            initialValue={
              userAccount?.agent?.type === AgentType.LEGAL ? userAccount.representingAgent?.id : userAccount?.agent?.id
            }
          >
            <AgentSelect
              optionsProps={{
                defaultOption: initialContract?.manager,
                agentTypes: [AgentType.NATURAL, AgentType.SELF_EMPLOYED],
              }}
              disabled={disablePrivilegedInput}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan} offset={colSpan * 3}>
          <AffiliatePartnerSelect
            formItemProps={{
              name: "affiliatePartnerId",
              label: t("contract.attrs.affiliatePartnerId"),
              rules: [validations.none],
            }}
            selectProps={{ disabled: disablePrivilegedInput, allowClear: true }}
          />
        </Col>
      </Row>

      <h4>{t("contract.attrs.gainerRecords._label")}:</h4>

      <ContractGainersFormSection initialContract={initialContract} disablePrivilegedInput={!!disablePrivilegedInput} />
    </Card>
  );
};

export default ContractFormAgentsSection;
